import { useMemo } from 'react';
import { RenderBalanceInColor } from '@/components/features/accounts/account-utils';
import { MovementChartData, MovementType } from '@/domain/accounts/account';
import { convertToDigitStr } from '@/utils/helpers';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ChartDataPoint {
  date: string
  dateShort: string
  amount: number
  type: MovementType
  balanceAfter: number
  description: string
  credit: number
  debit: number
}

interface BalanceTimelineChartProps {
  chartData: MovementChartData[]
}

const formatMovementDate = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date)
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
}

const buildChartData = (chartData: MovementChartData[]): ChartDataPoint[] => {
  return chartData
    .sort((a, b) => new Date(a.movementDate).getTime() - new Date(b.movementDate).getTime())
    .map((m) => ({
      date: m.movementDate.toISOString(),
      dateShort: formatMovementDate(m.movementDate),
      amount: m.amount,
      type: m.type,
      balanceAfter: m.balanceAfter,
      description: '',
      credit: m.type === MovementType.Credit ? m.amount : 0,
      debit: m.type === MovementType.Debit ? m.amount : 0,
    }))
}

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; payload: ChartDataPoint }> }) => {
  if (!active || !payload?.[0]) return null

  const data = payload[0].payload

  return (
    <div className="rounded-lg border bg-background p-3 shadow-sm">
      <p className="text-sm font-medium text-muted-foreground">{data.dateShort}</p>
      <p className="text-sm font-semibold mt-1">{data.description}</p>
      <div className="mt-2 space-y-1">
        {data.credit > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Crédito:</span>
            <span className="text-sm font-mono text-green-600">+${data.credit.toLocaleString('es-AR')}</span>
          </div>
        )}
        {data.debit > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Débito:</span>
            <span className="text-sm font-mono text-red-600">-${data.debit.toLocaleString('es-AR')}</span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t">
          <span className="text-xs text-muted-foreground">Saldo:</span>
          <RenderBalanceInColor balance={data.balanceAfter} />
        </div>
      </div>
    </div>
  )
}

export const BalanceTimelineChart = ({ chartData }: BalanceTimelineChartProps) => {
  const data = useMemo(() => buildChartData(chartData), [chartData])

  const minMovement = useMemo(() => Math.min(...data.map(d => d.balanceAfter), Infinity), [data])
  const maxMovement = useMemo(() => Math.max(...data.map(d => d.balanceAfter), -Infinity), [data])

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">Sin movimientos en el período seleccionado</p>
      </div>
    )
  }

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
        >
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
        <XAxis
          dataKey="dateShort"
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          interval="preserveStartEnd"
        />
        <YAxis
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          tickFormatter={(value: number) => `${convertToDigitStr(value)}`}
          width={100}
          axisLine={false}
          tickLine={false}
          domain={[minMovement, maxMovement]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="balanceAfter"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          fill="hsl(var(--primary))"
          fillOpacity={0.08}
          activeDot={{ r: 5, stroke: 'hsl(var(--primary))', strokeWidth: 2, fill: 'hsl(var(--background))' }}
        />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
